import type jsPDF from 'jspdf';
import { api } from '../lib/api';

/**
 * Filing an exported PDF where it can be found again.
 *
 * ⭐ His ruling 2026-09-09: *"file each export into the deal room automatically."* The PDF is
 * built here in the browser, so after it is saved to his machine its bytes go to the backend,
 * which files them against the property's deal (deal + Deal Room, internal) or, when the
 * property has no deal, against the property. Whatever happens, THE DOWNLOAD IS NEVER LOST: a
 * filing failure is returned as a message for the screen, not thrown after the save.
 */

export type ExportKind = 'Investment Analysis' | 'Valuation Summary' | 'Trade Area Report';

export interface FiledWhere {
  where: 'deal_room' | 'property' | 'master_property';
  dealId: string | null;
  dealName: string | null;
  title: string;
  linkId: string;
}

export interface ExportOutcome {
  fileName: string;
  /** Where it was filed, or null when filing failed - `error` then says why. */
  filed: FiledWhere | null;
  error: string | null;
}

/** The document's bytes as base64, in 32 KB slices so a 9 MB PDF does not blow the call stack. */
export const pdfToBase64 = (doc: jsPDF): string => {
  const bytes = new Uint8Array(doc.output('arraybuffer'));
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(binary);
};

export async function fileExportedPdf(
  doc: jsPDF,
  input: { kind: ExportKind; fileName: string; propertyId?: string | null; masterPropertyId?: string | null; dealId?: string | null }
): Promise<ExportOutcome> {
  try {
    const res = await api.fileExport({
      property_id: input.propertyId ?? null,
      master_property_id: input.masterPropertyId ?? null,
      deal_id: input.dealId ?? null,
      kind: input.kind,
      filename: input.fileName,
      pdf_base64: pdfToBase64(doc),
    });
    return {
      fileName: input.fileName,
      filed: {
        where: res.filed.where,
        dealId: res.filed.deal_id,
        dealName: res.filed.deal_name,
        title: res.filed.title,
        linkId: res.filed.link_id,
      },
      error: null,
    };
  } catch (e) {
    return { fileName: input.fileName, filed: null, error: e instanceof Error ? e.message : String(e) };
  }
}

/** The one line a screen shows after an export: where it went, or why it did not. */
export const describeOutcome = (o: ExportOutcome): string => {
  if (!o.filed) return `Saved as "${o.fileName}" - not filed: ${o.error ?? 'unknown error'}`;
  if (o.filed.where === 'deal_room') {
    return `Saved and filed to the Deal Room${o.filed.dealName ? ` (${o.filed.dealName})` : ''} as "${o.filed.title}"`;
  }
  if (o.filed.where === 'master_property') return `Saved and filed to this property record as "${o.filed.title}"`;
  return `Saved and filed to this property (it has no deal yet) as "${o.filed.title}"`;
};
